import React, { useEffect, useMemo, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Progress, Table as RSTable } from 'reactstrap';
import Swal from 'sweetalert2';
import { pdf } from '@react-pdf/renderer';
import { getCasosRead, postCasosFinalizados } from '../../../helpers/backend';
import { generateActaVisitaPDFBlob, generateActaVisitaPDFBlobWithoutImages } from '../../../components/ActaVisitaPDF';
import TiqueteCorrespondenciaPDF from '../../../components/TiqueteCorrespondenciaPDF';

// Utilidad para parsear posibles strings JSON
const parseMaybeJSON = (value, fallback = []) => {
  if (!value) return fallback;
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try { return JSON.parse(value); } catch { return fallback; }
  }
  return fallback;
};

// Construir datos de Acta como en CasosDetails
const buildActaData = (caso) => {
  const informeBase = Array.isArray(caso?.informe) ? (caso.informe[0] || {}) : (caso?.informe || {});

  const actaData = {
    fecha_visita: informeBase.fecha_visita || new Date().toISOString().split('T')[0],
    hora_visita: informeBase.hora_visita || new Date().toTimeString().slice(0, 5),
    ciudad_visita: informeBase.ciudad_visita || 'Bogotá',
    dia_visita: informeBase.dia_visita || new Date().getDate().toString(),
    mes_visita: informeBase.mes_visita || new Date().toLocaleDateString('es-ES', { month: 'long' }),
    año_visita: informeBase.año_visita || new Date().getFullYear().toString(),
    archivo_no: informeBase.archivo_no || '',
    no_pqrs: informeBase.no_de_pqrs || caso.numero_reclamo || '',
    cuenta_suscriptor: informeBase.cuenta_suscriptor || '',
    cuenta_energia: informeBase.cuenta_energia || '',
    direccion: informeBase.direccion || (caso.cliente?.[0]?.address?.[0]?.address || ''),
    nombre_cliente: informeBase.nombre_cliente || (caso.cliente?.[0]?.full_name || ''),
    direccion_informe: informeBase.direccion_informe || '',

    direccion_predio: informeBase.direccion_predio || '',
    estrato_predio: informeBase.estrato_predio || '',
    localidad_predio: informeBase.localidad_predio || '',
    actualizacion_nombre_suscriptor: informeBase.actualizacion_nombre_suscriptor || '',

    unidades_residenciales_detalle: parseMaybeJSON(informeBase.unidades_residenciales_detalle, []),
    producto_nombre: informeBase.producto_nombre || '',
    producto_detalle: parseMaybeJSON(informeBase.producto_detalle, []),
    unidades_no_residenciales_detalle: parseMaybeJSON(informeBase.unidades_no_residenciales_detalle, []),
    producto_unr: informeBase.producto_unr || '',
    recipientes_detalle: parseMaybeJSON(informeBase.recipientes_detalle, []),

    abandonado: !!informeBase.abandonado,
    suscriptor_inactivo: !!informeBase.suscriptor_inactivo,
    demolido: !!informeBase.demolido,
    en_construccion: !!informeBase.en_construccion,
    en_remodelacion: !!informeBase.en_remodelacion,
    inexistente: !!informeBase.inexistente,
    inhabitable_o_en_ruinas: !!informeBase.inhabitable_o_en_ruinas,
    lote: !!informeBase.lote,
    minimas_condiciones: !!informeBase.minimas_condiciones,
    programa_del_icbf: !!informeBase.programa_del_icbf,
    descuento_puerta_a_puerta: !!informeBase.descuento_puerta_a_puerta,
    se_cierra_pqr_como_no_atendido: !!informeBase.se_cierra_pqr_como_no_atendido,
    areas_comunes: !!informeBase.areas_comunes,
    predio_que_se_observa_vacio: !!informeBase.predio_que_se_observa_vacio,
    predio_se_observa_ocupado: !!informeBase.predio_se_observa_ocupado,
    cuenta_suscriptor_activo: !!informeBase.cuenta_suscriptor_activo,
    causa_demolicion: informeBase.causa_demolicion || '',
    suscriptor_activo_detalles: informeBase.suscriptor_activo_detalles || '',

    tipos_residuos: {
      escombros: !!informeBase.escombros,
      especiales: !!informeBase.especiales,
      para_reuso: !!informeBase.para_relleno,
      para_escombrera: !!informeBase.para_escombrera,
      enlonado: !!informeBase.enlonado,
      a_granel: !!informeBase.a_granel,
    },
    tipo_recoleccion: informeBase.tipo_recoleccion || '',
    valor_m3: informeBase.valor_m3 || '',
    m3_gratis: !!informeBase.m3_gratis,
    cancelado: !!informeBase.cancelado,

    observaciones: informeBase.observaciones || '',
    representante_nombre: informeBase.representante_nombre || '',
    representante_cedula: informeBase.representante_cedula || '',
    representante_firma: informeBase.representante_firma,
    representante_telefono_celular: informeBase.representante_telefono_celular || '',
    ciudad_limpia_nombre: caso.contacto?.[0]?.full_name || caso.agente?.full_name || '',
    ciudad_limpia_codigo: informeBase.ciudad_limpia_codigo || caso.contacto?.[0]?.codigo_visitador || caso.agente?.codigo_visitador || '',
    ciudad_limpia_firma: informeBase.ciudad_limpia_firma,
    ciudad_limpia_telefono: informeBase.ciudad_limpia_telefono || '',
    anotaciones: informeBase.anotaciones || '',
    sin_firma: !!informeBase.sin_firma,
  };

  return actaData;
};

// Resuelve URL de imagen igual que CorrespondenciaModal
const resolveImgUrl = (imgUrl) => {
  if (!imgUrl) return null;
  if (imgUrl.startsWith('data:') || imgUrl.startsWith('http') || imgUrl.startsWith('blob:')) return imgUrl;
  const baseUrl = process.env.REACT_APP_API_URL?.split('/api')[0] || '';
  return `${baseUrl}${imgUrl.startsWith('/') ? imgUrl : `/${imgUrl}`}`;
};

// Construir datos para TiqueteCorrespondenciaPDF (igual que mapDataToPDF en CorrespondenciaModal)
const buildCorrespondenciaData = (caso) => {
  const informeBase = Array.isArray(caso?.informe) ? (caso.informe[0] || {}) : (caso?.informe || {});
  const meta = caso?.metadata?.correspondencia || {};
  const allImages = informeBase.images || caso?.images || [];

  let telefonoReclamante = meta.telefonos_reclamante;
  if (Array.isArray(telefonoReclamante)) {
    telefonoReclamante = telefonoReclamante.join(' / ');
  }

  const visitas = caso?.visitas || caso?.metadata?.visitas || [];
  const visitasProcesadas = visitas.map(v => ({
    funcionario: v.funcionario_nombre || v.nombre_funcionario || v.funcionario || '',
    codigo: v.funcionario_codigo || v.codigo || '',
    fecha: v.fecha || '',
    hora: v.hora || '',
    observaciones: v.observaciones || '',
  }));

  // Extraer imágenes por label (igual que CorrespondenciaModal)
  const selloImg = allImages.find(img => img.label === 'Sello Recibido');
  const cnpImg = allImages.find(img => img.label === 'CNP');
  const fotosImgs = allImages.filter(img => img.label === 'Evidencia Extra');

  return {
    informe: informeBase,
    guia: caso?.name || '',
    planilla: meta.planilla || '',
    no_resolucion: meta.no_resolucion || '',
    remitente: 'CIUDAD LIMPIA',
    destinatarioNombre: meta.nombre_reclamante || '',
    destinatarioDireccion: meta.direccion_reclamante || '',
    destinatarioTelefono: telefonoReclamante || '',
    destinatarioCedula: meta.cedula_reclamante || '',
    fecha_original: meta.fecha_original || '',
    fecha_limite: meta.fecha_limite || '',
    recibidoNombre: informeBase.representante_nombre || '',
    recibidoCedula: informeBase.representante_cedula || '',
    recibidoTelefono: informeBase.representante_telefono_celular || telefonoReclamante || '',
    fechaEntrega: informeBase.fecha_informe ? new Date(informeBase.fecha_informe) : new Date(),
    fecha_informe: informeBase.fecha_informe || null,
    hora_visita: informeBase.hora_visita || null,
    observaciones: [caso?.descripcion, caso?.observaciones, meta.observaciones]
      .filter(v => v && v !== 'null' && v !== 'undefined')
      .join(' ') || '',
    visitas: visitasProcesadas,
    representante_firma: informeBase.representante_firma || null,
    ciudad_limpia_firma: informeBase.ciudad_limpia_firma || null,
    ciudad_limpia_nombre: informeBase.ciudad_limpia_nombre || '',
    ciudad_limpia_codigo: informeBase.ciudad_limpia_codigo || '',
    locationImage: resolveImgUrl(
      informeBase.location_map_image ||
      allImages.find(img => img.label === 'Ubicación de Finalización')?.image ||
      allImages.find(img => img.label === 'Ubicación de Finalización')?.image_url ||
      allImages.find(img => String(img.image || img.image_url || '').includes('ubicacion'))?.image ||
      allImages.find(img => String(img.image || img.image_url || '').includes('ubicacion'))?.image_url ||
      null
    ),
    locationCoords: informeBase.latitude && informeBase.longitude
      ? `${informeBase.latitude}, ${informeBase.longitude}`
      : (caso?.coordenadas || null),
    funcionario_nombre: informeBase.ciudad_limpia_nombre || '',
    funcionario_codigo: informeBase.ciudad_limpia_codigo || '',
    conSello: !!selloImg,
    selloImage: resolveImgUrl(selloImg?.image || selloImg?.image_url),
    cnpImage: resolveImgUrl(cnpImg?.image || cnpImg?.image_url),
    fotosImages: fotosImgs.map(img => resolveImgUrl(img.image || img.image_url)).filter(Boolean),
    causas: {
      vacío: false,
      noHayQuienReciba: false,
      rehusado: false,
      direccionErrada: false,
      altoRiesgo: false,
      errorDatos: false,
    },
    motivo: meta.motivo || '',
    no_pqrs: meta.no_pqrs || '',
  };
};

const fileSizeKB = (file) => (file.size / 1024).toFixed(1);

const ProcesarInformesMasivos = ({ isOpen, onClose, selectedIds = [] }) => {
  const [isFetching, setIsFetching] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isReadyPreview, setIsReadyPreview] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);

  const [casosMap, setCasosMap] = useState({}); // id -> caso
  const [filesMap, setFilesMap] = useState({}); // id -> { sinImagenesFile, conImagenesFile, sinUrl, conUrl }

  const totalSteps = selectedIds.length || 0;
  const generatedCount = useMemo(() => Object.keys(filesMap).length, [filesMap]);

  // Cargar datos de casos
  const fetchCasos = async () => {
    if (!selectedIds || selectedIds.length === 0) return;
    setIsFetching(true);
    setError(null);
    try {
      const results = await Promise.all(selectedIds.map(async (id) => {
        const res = await getCasosRead(id);
        return { id, data: res.data };
      }));
      const map = {};
      results.forEach(({ id, data }) => { map[id] = data; });
      setCasosMap(map);
    } catch (err) {
      console.error('Error cargando casos:', err);
      setError('Error al cargar datos de los casos.');
    } finally {
      setIsFetching(false);
    }
  };

  // Generar PDFs por cada caso
  const generatePDFs = async () => {
    setIsGenerating(true);
    setError(null);
    setFilesMap({});
    try {
      for (const id of selectedIds) {
        const caso = casosMap[id];
        if (!caso) continue;
        const today = new Date().toISOString().split('T')[0];

        if (caso.tipo_orden === 'CORRESPONDENCIA') {
          // Correspondencia: usa TiqueteCorrespondenciaPDF
          const corrData = buildCorrespondenciaData(caso);
          const blob = await pdf(<TiqueteCorrespondenciaPDF data={corrData} />).toBlob();
          const file = new File([blob], `Correspondencia_${id}_${today}.pdf`, { type: 'application/pdf' });
          const url = URL.createObjectURL(file);
          setFilesMap(prev => ({
            ...prev,
            [id]: { sinImagenesFile: file, conImagenesFile: file, sinUrl: url, conUrl: url },
          }));
        } else {
          // Acta de Visita: flujo original sin cambios
          const actaData = buildActaData(caso);
          const adicionales = caso.informes_adicionales || caso.informesAdicionales || [];

          const sinResult = await generateActaVisitaPDFBlobWithoutImages(actaData, caso, adicionales);
          if (!sinResult.success) throw new Error(sinResult.error || 'Error generando PDF sin imágenes');

          const conResult = await generateActaVisitaPDFBlob(actaData, caso, adicionales);
          if (!conResult.success) throw new Error(conResult.error || 'Error generando PDF con imágenes');

          const fileSin = new File([sinResult.blob], `Acta_Visita_${id}_sin_imagenes_${today}.pdf`, { type: 'application/pdf' });
          const fileCon = new File([conResult.blob], `Acta_Visita_${id}_con_evidencias_${today}.pdf`, { type: 'application/pdf' });

          setFilesMap(prev => ({
            ...prev,
            [id]: { sinImagenesFile: fileSin, conImagenesFile: fileCon, sinUrl: URL.createObjectURL(fileSin), conUrl: URL.createObjectURL(fileCon) },
          }));
        }
      }
      setIsReadyPreview(true);
    } catch (err) {
      console.error('Error generando PDFs:', err);
      setError('Error generando los PDFs.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Enviar al backend
  const handleSend = async () => {
    if (!isReadyPreview) return;
    setIsSending(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('casos_ids', JSON.stringify(selectedIds));

      Object.entries(filesMap).forEach(([id, files]) => {
        formData.append(`informe_pdf_${id}`, files.sinImagenesFile, files.sinImagenesFile.name);
        formData.append(`informe_pdf_evidencias_${id}`, files.conImagenesFile, files.conImagenesFile.name);
      });

      await postCasosFinalizados(formData);
      await Swal.fire({
        title: 'Éxito',
        text: 'PDFs enviados correctamente al backend.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });
      onClose?.();
    } catch (err) {
      console.error('Error enviando PDFs:', err);
      setError('Error al enviar los PDFs al backend.');
      Swal.fire({ title: 'Error', text: 'Falló el envío de archivos.', icon: 'error' });
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsReadyPreview(false);
      setFilesMap({});
      fetchCasos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, JSON.stringify(selectedIds)]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>Generación masiva de PDFs</ModalHeader>
      <ModalBody>
        {error && (
          <div className="alert alert-danger" role="alert">{error}</div>
        )}

        <div className="mb-3">
          <strong>OTs seleccionadas:</strong> {selectedIds.length}
        </div>

        {/* Paso 1: Cargar casos */}
        <div className="mb-3">
          <div className="d-flex align-items-center gap-2">
            <Button color="secondary" size="sm" disabled={isFetching} onClick={fetchCasos}>Cargar datos</Button>
            {isFetching && <span>Cargando datos de casos...</span>}
          </div>
        </div>

        {/* Paso 2: Generar PDFs */}
        <div className="mb-3">
          <div className="d-flex align-items-center gap-2">
            <Button color="primary" size="sm" disabled={isGenerating || isFetching || Object.keys(casosMap).length === 0} onClick={generatePDFs}>Generar PDFs</Button>
            {isGenerating && <span>Generando PDFs ({generatedCount}/{totalSteps})...</span>}
          </div>
          {isGenerating && (
            <Progress value={Math.min(100, (generatedCount / (totalSteps || 1)) * 100)} className="mt-2" />
          )}
        </div>

        {/* Vista previa de archivos */}
        {isReadyPreview && (
          <div>
            <h6 className="mb-2">Archivos listos para enviar</h6>
            <RSTable bordered responsive size="sm">
              <thead>
                <tr>
                  <th>OT ID</th>
                  <th>PDF sin imágenes</th>
                  <th>PDF con evidencias</th>
                </tr>
              </thead>
              <tbody>
                {selectedIds.map((id) => {
                  const f = filesMap[id];
                  return (
                    <tr key={id}>
                      <td>{id}</td>
                      <td>
                        {f?.sinImagenesFile ? (
                          <div className="d-flex align-items-center gap-2">
                            <span>{f.sinImagenesFile.name} ({fileSizeKB(f.sinImagenesFile)} KB)</span>
                            <Button color="link" size="sm" onClick={() => window.open(f.sinUrl, '_blank')}>Ver</Button>
                          </div>
                        ) : 'Pendiente'}
                      </td>
                      <td>
                        {f?.conImagenesFile ? (
                          <div className="d-flex align-items-center gap-2">
                            <span>{f.conImagenesFile.name} ({fileSizeKB(f.conImagenesFile)} KB)</span>
                            <Button color="link" size="sm" onClick={() => window.open(f.conUrl, '_blank')}>Ver</Button>
                          </div>
                        ) : 'Pendiente'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </RSTable>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={isSending}>Cerrar</Button>
        <Button color="success" onClick={handleSend} disabled={!isReadyPreview || isSending}>Enviar al backend</Button>
      </ModalFooter>
    </Modal>
  );
};

export default ProcesarInformesMasivos;